/**
 * TanStack Query hook for the coach's suggested-sessions list — powers the
 * CoachCard on the learner dashboard. Each row is a mixed-type Suggestion
 * pointing at either a lesson (learn path) or a curriculum node (revise path).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type { SuggestionsResponse } from '@formency/shared';

// ─── Types ─────────────────────────────────────────────────────────────────

const DEFAULT_LIMIT = 10;
const DEFAULT_WINDOW_DAYS = 7;

// ─── Query key factory ─────────────────────────────────────────────────────

export const suggestionsKeys = {
  all: ['f', 'suggestions'] as const,
  me: (limit: number, windowDays: number) =>
    [...suggestionsKeys.all, 'me', limit, windowDays] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/**
 * Fetch the coach's suggested next sessions for the current user.
 *
 * The server-side coach service orchestrates 5 candidate queries (start /
 * refresh / breakthrough / push / build), scores and diversifies them, and
 * returns the top N. This hook just surfaces that list to the widget.
 *
 * Cached for 5 minutes; callers should invalidate `suggestionsKeys.all` after
 * session completion so the next suggestion set reflects the new state.
 */
export function useMySuggestions(options?: {
  enabled?: boolean;
  staleTime?: number;
  limit?: number;
  windowDays?: number;
}) {
  const limit = options?.limit ?? DEFAULT_LIMIT;
  const windowDays = options?.windowDays ?? DEFAULT_WINDOW_DAYS;
  return useQuery<SuggestionsResponse>({
    queryKey: suggestionsKeys.me(limit, windowDays),
    queryFn: () =>
      api.get<SuggestionsResponse>(`/me/suggestions?limit=${limit}&windowDays=${windowDays}`),
    staleTime: options?.staleTime ?? 5 * 60_000,
    enabled: options?.enabled,
  });
}

/**
 * Dismiss a just-launched suggestion so it doesn't re-surface when the
 * learner returns to the dashboard. Server-side (cross-device) with a default
 * 2-hour TTL. Invalidates the suggestions query on success so the next read
 * reflects the suppression.
 */
export function useDismissSuggestion() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, Error, { suggestionId: string; ttlSeconds?: number }>({
    mutationFn: (body) => api.post<{ ok: true }>('/me/suggestions/dismiss', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: suggestionsKeys.all });
    },
  });
}
